import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Languages, X } from "lucide-react";

/**
 * Selo "Acessibilidade de conteúdo" — popover discreto que lista os
 * recursos de leitura/tradução disponíveis. Contexto detectado pela
 * rota: artigos (blog/colunistas) mostram leitor de voz + fonte
 * dislexia; páginas de vídeo mostram VLibras + transcrição; todas as
 * páginas listam ABNT/APA e impressão como recursos globais.
 *
 * Visual: pílula com o ícone <Languages /> + texto "Acessibilidade".
 * Variant `compact` esconde o texto (usado no header em telas estreitas).
 */
const FEATURES = {
  // Páginas de artigo: blog + colunistas
  article: [
    { name: "Leitor de voz (TTS)", desc: "Síntese de voz do navegador em pt-BR. Play/Pause/Stop, controle de velocidade (0.8×–1.5×)." },
    { name: "Fonte para dislexia", desc: "Alterna a tipografia para OpenDyslexic com espaçamento ampliado." },
  ],
  // Páginas de vídeo
  episode: [
    { name: "VLibras (LIBRAS)", desc: "Tradução automática para Língua Brasileira de Sinais via widget oficial do gov.br. Ativado ao expandir a transcrição." },
    { name: "Transcrição textual", desc: "Texto completo do episódio para leitura e busca (Ctrl-F)." },
    { name: "Marcadores de tempo", desc: "Capítulos clicáveis no player do YouTube." },
  ],
  // Recursos globais (sempre disponíveis)
  global: [
    { name: "Citação acadêmica", desc: "Geração automática nos padrões ABNT, APA e BibTeX." },
    { name: "Impressão / PDF", desc: "Layout otimizado com cabeçalho e QR Code do JurisTube." },
    { name: "Navegação por teclado", desc: "Todos os controles são acessíveis via Tab/Enter." },
  ],
};

function detectContext(pathname) {
  if (pathname.startsWith("/episodio/")) return "episode";
  if (pathname.startsWith("/blog/") || pathname.startsWith("/colunistas/")) {
    // Heuristic: índices (`/blog`, `/colunistas`) não têm 2º segmento.
    const parts = pathname.split("/").filter(Boolean);
    return parts.length >= 2 ? "article" : null;
  }
  return null;
}

export default function AccessibilityBadge({ variant = "default", placement = "bottom" }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const context = detectContext(pathname);

  // Click outside → fecha o popover.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const contextual = context ? FEATURES[context] : [];
  const isCompact = variant === "compact";
  const isFooter = variant === "footer";

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          isFooter
            ? "inline-flex items-center gap-1.5 text-sky-400 hover:text-sky-300 transition text-xs"
            : "inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white border border-slate-800 hover:border-emerald-500/60 rounded-full px-2.5 py-1.5 transition whitespace-nowrap"
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Recursos de acessibilidade de conteúdo"
        data-testid="accessibility-badge"
      >
        <Languages className={`${isFooter ? "w-3 h-3" : "w-3.5 h-3.5"} text-emerald-400`} />
        {!isCompact && <span>Acessibilidade</span>}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Recursos de acessibilidade disponíveis"
          className={`absolute ${placement === "top" ? "bottom-full mb-2" : "top-full mt-2"} right-0 w-[300px] z-[60] rounded-xl border border-emerald-500/40 bg-slate-950/98 backdrop-blur shadow-2xl shadow-emerald-900/30 p-3`}
          data-testid="accessibility-popover"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-emerald-200 text-[11px] font-semibold uppercase tracking-wider">
              <Languages className="w-3.5 h-3.5" />
              Acessibilidade de conteúdo
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-500 hover:text-white"
              aria-label="Fechar"
              data-testid="accessibility-popover-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {contextual.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                Nesta página
              </div>
              <ul className="space-y-2">
                {contextual.map((f) => (
                  <li key={f.name} className="text-[12px]">
                    <div className="text-slate-100 font-medium leading-tight">{f.name}</div>
                    <div className="text-slate-400 leading-snug">{f.desc}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={contextual.length > 0 ? "pt-3 border-t border-slate-800" : ""}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
              Em todas as páginas
            </div>
            <ul className="space-y-2">
              {FEATURES.global.map((f) => (
                <li key={f.name} className="text-[12px]">
                  <div className="text-slate-100 font-medium leading-tight">{f.name}</div>
                  <div className="text-slate-400 leading-snug">{f.desc}</div>
                </li>
              ))}
            </ul>
          </div>

          <Link
            to="/acessibilidade"
            onClick={() => setOpen(false)}
            className="mt-3 block text-center text-[11px] text-emerald-300 hover:text-emerald-200 font-medium border-t border-slate-800 pt-2.5"
            data-testid="accessibility-learn-more"
          >
            Saiba mais →
          </Link>
        </div>
      )}
    </div>
  );
}
